import { useState } from 'react';
import { line, Color, DEFAULT_CURRENCY_CODE, DEFAULT_PAGE_SIZE, Size, formatDateTime, t, type ColumnDef, type LabourLineTotal, type LabourRow, type PageSize, type SortState, type WorkOrderLabourAnswer, viewerScopeKey } from '@bms2/shared';
import { Button, DatePicker, NumericInput, Select, TextInput } from '../../components/controls';
import { ListViewer } from '../../components/composites/ListViewer';
import { AlertBanner } from '../../components/controls/AlertBanner';
import { Modal } from '../../components/modals/Modal';
import { Money } from '../../components/Money';
import { useDateFormat, useViewerZone } from '../../shell/ViewerZone';
import { post, useApi } from './api';
import { WORK_ORDERS_PATH } from './WorkOrderPanels';
import styles from './RecordPage.module.css';
import own from './WorkOrderLabour.module.css';

/** 🔨 Relative to the API's own root, never prefixed with /api (piece 1). */
export const workOrderLabourPath = (workOrderId: string): string => `${WORK_ORDERS_PATH}/${workOrderId}/labour`;
export const workOrderRecordTimePath = (workOrderId: string, lineId: string): string => `${WORK_ORDERS_PATH}/${workOrderId}/lines/${lineId}/time`;
export const workOrderCorrectTimePath = (workOrderId: string, entryId: string): string => `${WORK_ORDERS_PATH}/${workOrderId}/time/${entryId}/correct`;

interface Recording {
  line: LabourLineTotal;
  employmentId: string;
  day: string;
  hours: number | null;
  from: string;
  to: string;
  note: string;
}

const hoursText = (n: number | null) => (n === null ? '' : `${n} h`);

/**
 * 🔒 The Labour tile (decision 1908; T1201): every time entry on the job with
 * who, the line or Travel, in, out, hours, how it was captured, the rates on
 * the day, the cost and the invoice that billed it; each worker's typed hours
 * as their own row, read as billing or as not billed while entries exist
 * (decision 1903); and each line's totals beside its budget. Record Time sits
 * on each line (decision 1904) and Raise a Correction on the List Viewer's
 * action bar for one ticked entry (decision 1906; piece 4). 🔨 Cost is drawn only where the server sent it, and reads Withheld
 * where it did not.
 */
export function WorkOrderLabour({ workOrderId, apiBaseUrl = '', onChanged }: { workOrderId: string; apiBaseUrl?: string; onChanged: () => void }) {
  const read = useApi<WorkOrderLabourAnswer>(workOrderLabourPath(workOrderId), apiBaseUrl);
  const zone = useViewerZone();
  const dateFormat = useDateFormat();
  const [recording, setRecording] = useState<Recording | null>(null);
  const [correcting, setCorrecting] = useState<{ entryId: string; who: string; hours: number | null; reason: string } | null>(null);
  const [sort, setSort] = useState<SortState | null>(ROUTE_ORDER);
  const [pageSize, setPageSize] = useState<PageSize>(DEFAULT_PAGE_SIZE);
  const [picked, setPicked] = useState<Record<string, readonly string[]>>({});
  /** 🔒 A refused write reads inside the window it came from, which stays open with what was typed (decision 3063). */
  const [refusal, setRefusal] = useState<string | null>(null);
  const answer = read.state.kind === 'ready' ? read.state.data : null;
  if (!answer) return null;
  const rows = Array.isArray(answer.rows) ? answer.rows : [];
  const lines = Array.isArray(answer.lines) ? answer.lines : [];
  const currencyCode = answer.currencyCode || DEFAULT_CURRENCY_CODE;
  const at = (instant: string | null) => (instant ? formatDateTime(instant, zone, dateFormat) : '');

  // 🔨 A refused write keeps its window open with what was typed; only a written one closes it.
  const wrote = (outcome: { ok: true } | { ok: false; message: string }): boolean => {
    if (!outcome.ok) {
      setRefusal(outcome.message);
      return false;
    }
    setRefusal(null);
    read.reload?.();
    onChanged();
    return true;
  };
  const record = async () => {
    if (!recording) return;
    const timed = recording.from && recording.to;
    if (wrote(await post(workOrderRecordTimePath(workOrderId, recording.line.lineId), {
      employmentId: recording.employmentId, workDate: recording.day || null, timeZone: zone, note: recording.note,
      ...(timed ? { startsAt: recording.from, endsAt: recording.to } : { hours: recording.hours }),
    }, apiBaseUrl))) setRecording(null);
  };
  const correct = async () => {
    if (!correcting) return;
    if (wrote(await post(workOrderCorrectTimePath(workOrderId, correcting.entryId), { hours: correcting.hours, reason: correcting.reason }, apiBaseUrl))) setCorrecting(null);
  };
  const openRecording = (total: LabourLineTotal) => {
    const mine = total.workers.find((worker) => worker.employmentId === answer.viewerEmploymentId);
    const first = answer.canRecordForOthers ? total.workers[0] : mine;
    setRecording({ line: total, employmentId: mine?.employmentId ?? first?.employmentId ?? '', day: '', hours: null, from: '', to: '', note: '' });
  };

  /*
   * 🔒 Each line's rows sit in the List Viewer like every other table (piece 4):
   * a header re-sorts them, the route's order holds until one does, and Raise a
   * Correction stands on the action bar once exactly one entry is ticked.
   */
  const tableFor = (key: string, group: LabourRow[]) => {
    const chosen = (picked[key] ?? []).map((id) => group.find((row) => row.id === id && row.kind === 'entry'));
    const entry = chosen.length === 1 && chosen[0]?.kind === 'entry' && chosen[0].correctsTimeEntryId === null ? chosen[0] : null;
    return (
      <ListViewer
        viewStore={{ scopeKey: viewerScopeKey('work-order-labour'), apiBaseUrl, param: 'labourView' }}
        recordType={t('workOrder.labour.recordType')}
        views={[{ id: 'all', name: t('workOrder.labour.view') }]}
        currentViewId="all"
        onViewChange={() => undefined}
        columns={COLUMNS}
        rows={group.map((row, position) => labourCells(row, position, at))}
        idOf={(cell) => cell.id}
        read={(cell, column) => cell[column as keyof LabourCells]}
        sort={sort}
        onSortChange={setSort}
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
        emptyAction={t('workOrder.labour.none')}
        currencyCode={currencyCode}
        rowStyle={(cell) => (cell.typed ? { name: 'typed', className: own.typedRow ?? '' } : null)}
        selectable={answer.canCorrect}
        onSelectionChange={(ids) => setPicked((now) => ({ ...now, [key]: ids }))}
        selectionActions={entry ? (
          <Button size={Size.Small} color={Color.Inactive} text={[t('workOrder.time.correct')]} actions={[() => setCorrecting({ entryId: entry.id, who: entry.personName, hours: null, reason: '' })]} />
        ) : <span className={styles.hint}>{t('workOrder.labour.chooseOne')}</span>}
        bandless
      />
    );
  };

  const unlined = rows.filter((row) => row.kind === 'entry' && row.lineId === null);
  return (
    <section data-work-order-labour={workOrderId}>
      {rows.length === 0 && lines.length === 0 ? <p className={styles.hint} data-labour-none="true">{t('workOrder.labour.none')}</p> : null}
      {answer.canCorrect && rows.some((row) => row.kind === 'entry' && row.correctsTimeEntryId === null) ? <p className={styles.hint} data-labour-correct-hint="true">{t('workOrder.labour.tickToCorrect')}</p> : null}
      {lines.map((total) => {
        const group = rows.filter((row) => row.lineId === total.lineId);
        return (
          <div key={total.lineId} data-labour-line={total.lineId}>
            <p className={own.lineTotals}>
              <span className={styles.sectionTitle}>{total.lineLabel}</span>
              <span className={styles.hint} data-labour-line-actual={total.actualHours}>
                {line(t('workOrder.labour.budget'), total.budgetedHours === null ? t('workOrder.labour.noBudget') : hoursText(total.budgetedHours), '·', t('workOrder.labour.actual'), hoursText(total.actualHours), `(${line(t('workOrder.labour.typed'), hoursText(total.typedHours), '·', t('workOrder.labour.entries'), hoursText(total.entryHours))})`, '·', t('workOrder.labour.billed'), hoursText(total.billedHours), total.remainingHours === null ? null : `· ${line(t('workOrder.labour.remaining'), hoursText(total.remainingHours))}`)}
              </span>
              {answer.canRecord && (answer.canRecordForOthers || total.workers.some((worker) => worker.employmentId === answer.viewerEmploymentId)) ? (
                <Button size={Size.Small} color={Color.Navy} text={[t('workOrder.time.record')]} actions={[() => openRecording(total)]} />
              ) : null}
            </p>
            {group.length > 0 ? tableFor(total.lineId, group) : null}
          </div>
        );
      })}
      {unlined.length > 0 ? (
        <div data-labour-line="none">
          <p className={own.lineTotals}><span className={styles.sectionTitle}>{t('workOrder.labour.unlined')}</span></p>
          {tableFor('none', unlined)}
        </div>
      ) : null}
      <p className={own.lineTotals} data-labour-job={answer.job.actualHours}>
        <span className={styles.sectionTitle}>{t('workOrder.labour.jobTotal')}</span>
        <span className={styles.hint}>
          {line(t('workOrder.labour.budget'), answer.job.budgetedHours === null ? t('workOrder.labour.noBudget') : hoursText(answer.job.budgetedHours), '·', t('workOrder.labour.actual'), hoursText(answer.job.actualHours), `(${line(t('workOrder.labour.typed'), hoursText(answer.job.typedHours), '·', t('workOrder.labour.entries'), hoursText(answer.job.entryHours))})`, '·', t('workOrder.labour.billed'), hoursText(answer.job.billedHours), answer.job.remainingHours === null ? null : `· ${line(t('workOrder.labour.remaining'), hoursText(answer.job.remainingHours))}`)}
        </span>
        {answer.job.costCents === undefined ? null : <span className={styles.hint}>{t('workOrder.labour.cost')} <Money cents={answer.job.costCents} currencyCode={currencyCode} /></span>}
      </p>

      <Modal
        open={recording !== null}
        title={t('workOrder.time.recordTitle')}
        {...(recording ? { subtitle: recording.line.lineLabel } : {})}
        onCancel={() => { setRefusal(null); setRecording(null); }}
        dataModal="work-order-record-time"
        actions={[
          { kind: 'primary', label: t('settings.save'), disabled: !recording?.note.trim() || !recording.employmentId || (!(recording.hours && recording.hours > 0) && !(recording.from && recording.to)), onAct: () => void record() },
          { kind: 'cancel', label: t('modal.cancel'), onAct: () => { setRefusal(null); setRecording(null); } },
        ]}
      >
        {recording && refusal ? <AlertBanner tone="error" message={refusal} action={{ label: t('modal.dismiss'), onAction: () => setRefusal(null) }} /> : null}
        <p className={styles.hint}>{t('workOrder.time.hint')}</p>
        <Select
          size={Size.Medium}
          label={t('workOrder.time.worker')}
          value={recording?.employmentId ?? ''}
          onChange={(employmentId) => setRecording(recording ? { ...recording, employmentId } : null)}
          options={(recording?.line.workers ?? []).filter((worker) => answer.canRecordForOthers || worker.employmentId === answer.viewerEmploymentId).map((worker) => ({ value: worker.employmentId, label: worker.name }))}
          dataAttribute={{ 'data-record-time-worker': 'true' }}
          required
        />
        <DatePicker size={Size.Medium} label={t('workOrder.time.day')} value={recording?.day ?? ''} onChange={(day) => setRecording(recording ? { ...recording, day } : null)} name="record-time-day" />
        <NumericInput size={Size.Small} kind="number" decimals={2} label={t('workOrder.time.hours')} value={recording?.hours ?? null} onChange={(hours) => setRecording(recording ? { ...recording, hours } : null)} name="record-time-hours" />
        <DatePicker size={Size.Medium} mode="datetime" label={t('workOrder.time.from')} value={recording?.from ?? ''} onChange={(from) => setRecording(recording ? { ...recording, from } : null)} name="record-time-from" />
        <DatePicker size={Size.Medium} mode="datetime" label={t('workOrder.time.to')} value={recording?.to ?? ''} onChange={(to) => setRecording(recording ? { ...recording, to } : null)} name="record-time-to" {...(recording?.from ? { min: recording.from } : {})} />
        <TextInput size={Size.Large} label={t('workOrder.time.note')} value={recording?.note ?? ''} onChange={(note) => setRecording(recording ? { ...recording, note } : null)} name="record-time-note" required />
      </Modal>

      <Modal
        open={correcting !== null}
        title={t('workOrder.time.correctTitle')}
        {...(correcting ? { subtitle: correcting.who } : {})}
        onCancel={() => { setRefusal(null); setCorrecting(null); }}
        dataModal="work-order-correct-time"
        actions={[
          { kind: 'primary', label: t('workOrder.time.correct'), disabled: !correcting?.reason.trim() || !correcting.hours, onAct: () => void correct() },
          { kind: 'cancel', label: t('modal.cancel'), onAct: () => { setRefusal(null); setCorrecting(null); } },
        ]}
      >
        {correcting && refusal ? <AlertBanner tone="error" message={refusal} action={{ label: t('modal.dismiss'), onAction: () => setRefusal(null) }} /> : null}
        <p className={styles.hint}>{t('workOrder.time.correctHint')}</p>
        <NumericInput size={Size.Small} kind="number" decimals={2} label={t('workOrder.time.correctHours')} value={correcting?.hours ?? null} onChange={(hours) => setCorrecting(correcting ? { ...correcting, hours } : null)} name="correct-time-hours" required />
        <TextInput size={Size.Large} label={t('workOrder.time.correctReason')} value={correcting?.reason ?? ''} onChange={(reason) => setCorrecting(correcting ? { ...correcting, reason } : null)} name="correct-time-reason" required />
      </Modal>
    </section>
  );
}

/**
 * 🔒 The tile's columns as they stood (decision 1908): who, the line, in, out, hours, how, the three money figures and the invoice.
 * The figures and the invoice take their own width first, so a tile narrower than ten columns truncates the words, never a sum.
 */
const COLUMNS: readonly ColumnDef[] = [
  { key: 'who', label: t('workOrder.labour.who'), role: 'primary', type: 'text' },
  { key: 'line', label: t('workOrder.labour.line'), role: 'secondary', type: 'text' },
  { key: 'in', label: t('workOrder.labour.in'), role: 'secondary', type: 'text' },
  { key: 'out', label: t('workOrder.labour.out'), role: 'secondary', type: 'text' },
  { key: 'hours', label: t('workOrder.labour.hours'), role: 'secondary', type: 'text', fit: true },
  { key: 'how', label: t('workOrder.labour.how'), role: 'secondary', type: 'text' },
  { key: 'billingRate', label: t('workOrder.labour.billingRate'), role: 'secondary', type: 'currency', fit: true },
  { key: 'costRate', label: t('workOrder.labour.costRate'), role: 'secondary', type: 'currency', fit: true },
  { key: 'cost', label: t('workOrder.labour.cost'), role: 'secondary', type: 'currency', fit: true },
  { key: 'invoice', label: t('workOrder.labour.invoice'), role: 'secondary', type: 'text', fit: true },
];

/** 🔒 The route's order, the position a row arrived in, until a header is clicked. */
const ROUTE_ORDER: SortState = { key: 'position', direction: 'asc' };

/** One row as the List Viewer reads it. Money is cents; a cost the viewer is refused is the word Withheld (decision 0128). */
interface LabourCells {
  id: string;
  position: number;
  typed: boolean;
  who: string;
  line: string;
  in: string;
  out: string;
  hours: string;
  how: string;
  billingRate: number | null;
  costRate: number | string | null;
  cost: number | string | null;
  invoice: string;
}

function labourCells(row: LabourRow, position: number, at: (instant: string | null) => string): LabourCells {
  const withheld = t('workOrder.labour.withheld');
  const money = {
    billingRate: row.billingRateCents,
    costRate: row.costWithheld ? withheld : (row.costRateCents ?? null),
    cost: row.costWithheld ? withheld : (row.costCents ?? null),
  };
  if (row.kind === 'typed') {
    return {
      id: `typed-${row.id}`, position, typed: true, who: row.personName, line: row.lineLabel, in: '', out: '', hours: hoursText(row.hours),
      how: line(t('workOrder.labour.typedRow'), '·', row.counts ? t('workOrder.labour.typedBills') : t('workOrder.labour.typedNotBilled')),
      ...money, invoice: '',
    };
  }
  const aside = row.correctionReason ? line('·', t('workOrder.labour.correction'), row.correctionReason) : row.note ? line('·', row.note) : null;
  return {
    id: row.id,
    position,
    typed: false,
    who: row.personName,
    line: row.lineLabel ?? (row.entryType === 'Travel' ? t('workOrder.labour.travel') : t('workOrder.labour.jobOnly')),
    in: row.startedAt ? at(row.startedAt) : row.workDate,
    out: at(row.endedAt),
    hours: hoursText(row.hours),
    how: line(row.sourceLabel, row.approvalStatus === 'Pending' ? line('·', t('workOrder.labour.pending')) : null, aside),
    ...money,
    invoice: row.invoice ? (row.invoice.invoiceNumber ?? t('workOrder.labour.draftInvoice')) : t('workOrder.labour.unbilled'),
  };
}
